import { SyncCombinatorGuard } from './../../guards/sync-combinator-guard/sync-combinator-guard';
import { RouteGuard } from '../../../../ng2/routing/guards/route-guard/route-guard';
import { TogglesGuard } from '../../../../ng2/routing/guards/toggles-guard/toggles-guards';
import { CurrentUserGuard } from './../../guards/current-user-guard/current-user-guard';
import { EntityGuard } from './../../guards/entity-guard/entity-guard';
import { PusherResolver } from '../../resolvers/pusher/pusher-resolver';
import { SchoolSsvFilterResolver } from '../../resolvers/school-ssv/school-ssv-filter/school-ssv-filter.resolver';
import { SchoolSsvSortResolver } from '../../resolvers/school-ssv/school-ssv-sort/school-ssv-sort.resolver';
import { CurrentEntityGuard } from '../../guards/current-entity-guard/current-entity-guard';
import { SchoolStandardLayoutComponent } from 'Src/ng2/school/layouts/school-standard-layout/school-standard-layout.component';
import { ssvMatcher } from '../../url-matchers/ssv-matcher/ssv-matcher';
import { CurrentSdcSchoolGuard } from '../../guards/sdc/current-sdc-school-guard/current-sdc-school-guard';
import { PusherGuard } from '../../guards/pusher-guard/pusher-guard';
import { SchoolSingleColumnLayoutComponent } from 'Src/ng2/school/layouts/school-single-column-layout/school-single-column-layout.component';
import { FeatureToggleGuard } from '../../guards/feature-toggle-guard/feature-toggle-guard';
import { ExploreAllViewGuard } from '../../guards/explore-all-view-guard/explore-all-view-guard';
import { PartnerTypes } from 'Src/ng2/shared/typings/interfaces/partner.interface';
import { SessionStorageEntityIdResolver } from '../../resolvers/session-storage-entityid/session-storage-entityid.resolver';
import { USER_ROLE_PERMISSIONS_FOR_GUARDS } from '../../guards/user-role-permissions-for-route-guards.constant';
import { KimsTrackerAccessGuard } from '../../guards/kims-tracker-access-guard/kims-tracker-access-guard';

export const entityRoute = {
  path: ':entity',
  url: ':entity',
  authenticationRequired: true,
  rolePermissions: USER_ROLE_PERMISSIONS_FOR_GUARDS[':entity'],
  canActivate: [SyncCombinatorGuard],
  data: {
    guards: [EntityGuard, RouteGuard, CurrentUserGuard, TogglesGuard],
  },
  resolve: {
    entityId: SessionStorageEntityIdResolver,
  },
  children: [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: '/not-found',
    },
    {
      path: 'school',
      url: '/school',
      authenticationRequired: true,
      rolePermissions: ['all'],
      canActivate: [SyncCombinatorGuard],
      data: {
        guards: [RouteGuard, CurrentUserGuard, TogglesGuard],
      },
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: '/not-found',
        },
        {
          path: ':schoolId',
          canActivate: [SyncCombinatorGuard],
          data: {
            guards: [CurrentEntityGuard, CurrentSdcSchoolGuard, TogglesGuard, PusherGuard],
          },
          resolve: {
            pusher: PusherResolver,
          },
          children: [
            {
              path: '',
              pathMatch: 'full',
              redirectTo: 'lists',
            },
            {
              path: 'lists',
              component: SchoolStandardLayoutComponent,
              loadChildren: () =>
                import('../../../school/lists/school-lists-routing.module').then(m => m.SchoolListRoutingModule),
              data: {
                contextPartnerType: PartnerTypes.SCHOOL,
                // switching between school sub-entities requires angular to reload homepage components so that the school
                // in the store is aligned with the url - see appRouteReuseStrategy.ts
                refreshComponent: (future, curr) => {
                  return future?.params?.schoolId !== curr?.params?.schoolId;
                },
              },
            },
            {
              path: 'postsecondary',
              component: SchoolStandardLayoutComponent,
              canActivate: [FeatureToggleGuard],
              data: {
                toggles: ['TOGGLE_CONTENT_AREA_DASHBOARD'],
                onFailureRedirect: 'home',
                contextPartnerType: PartnerTypes.SCHOOL,
              },
              loadChildren: () =>
                import('../../../school/content-area/postsecondary/postsecondary-cad-routing.module').then(m => m.PostsecondaryCadRoutingModule),
            },
            {
              path: 'college-now',
              component: SchoolStandardLayoutComponent,
              loadChildren: () =>
                import('../../../school/content-area/college-now/college-now-cad-routing.module').then(m => m.CollegeNowCadRoutingModule),
              data: {
                contextPartnerType: PartnerTypes.SCHOOL,
              },
            },
            {
              path: 'attendance',
              component: SchoolStandardLayoutComponent,
              canActivate: [FeatureToggleGuard],
              data: {
                toggles: ['TOGGLE_ATTENDANCE_CONTENT_AREA_DASHBOARD'],
                onFailureRedirect: 'home',
                contextPartnerType: PartnerTypes.SCHOOL,
              },
              loadChildren: () =>
                import('../../../school/content-area/attendance/attendance-cad-routing.module').then(m => m.AttendanceCadRoutingModule),
            },
            {
              matcher: ssvMatcher, // could be '' or :studentId based on existing logic
              component: SchoolSingleColumnLayoutComponent,
              resolve: {
                filter: SchoolSsvFilterResolver,
                sort: SchoolSsvSortResolver,
              },
              data: {
                contextPartnerType: PartnerTypes.SCHOOL,
              },
              loadChildren: () =>
                import('../../../student/student.module').then(m => m.StudentModule),
            },
            {
              path: 'data-grid',
              data: {
                contextPartnerType: PartnerTypes.SCHOOL,
              },
              loadChildren: () =>
                import('../../../school/server-side-grid/server-side-grid.module').then(m => m.ServerSideGridModule),
            },
            {
              path: 'notes',
              component: SchoolStandardLayoutComponent,
              data: {
                onFailureRedirect: 'home',
                contextPartnerType: PartnerTypes.SCHOOL,
                origin: 'Notes Inbox',
              },
              loadChildren: () =>
                import('../../../school/notes/notes-page/notes-page.module').then(m => m.NotesPageModule),
            },
            {
              path: 'kims-tracker',
              component: SchoolStandardLayoutComponent,
              canActivate: [KimsTrackerAccessGuard],
              data: {
                contextPartnerType: PartnerTypes.SCHOOL,
              },
              loadChildren: () =>
                import('../../../school/content-area/postsecondary/kims-tracker/kims-tracker.module').then(m => m.KimsTrackerModule),
            },
          ],
        },
      ],
    },
    {
      path: 'explore-all',
      url: '/explore-all',
      authenticationRequired: true,
      rolePermissions: USER_ROLE_PERMISSIONS_FOR_GUARDS[':entity'],
      canActivate: [SyncCombinatorGuard],
      data: {
        guards: [ExploreAllViewGuard, RouteGuard],
        isExploreAll: true,
      },
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'lists',
        },
        {
          path: 'lists',
          component: SchoolStandardLayoutComponent,
          loadChildren: () =>
            import('../../../school/lists/school-lists-routing.module').then(m => m.SchoolListRoutingModule),
          canActivate: [ExploreAllViewGuard],
          data: {
            contextPartnerType: PartnerTypes.SCHOOL,
          },
        },
        {
          path: 'postsecondary',
          component: SchoolStandardLayoutComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_CONTENT_AREA_DASHBOARD'],
            onFailureRedirect: 'home',
            contextPartnerType: PartnerTypes.SCHOOL,
          },
          loadChildren: () =>
            import('../../../school/content-area/postsecondary/postsecondary-cad-routing.module').then(m => m.PostsecondaryCadRoutingModule),
        },
        {
          path: 'college-now',
          component: SchoolStandardLayoutComponent,
          loadChildren: () =>
            import('../../../school/content-area/college-now/college-now-cad-routing.module').then(m => m.CollegeNowCadRoutingModule),
          data: {
            contextPartnerType: PartnerTypes.SCHOOL,
          },
        },
        {
          path: 'attendance',
          component: SchoolStandardLayoutComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ATTENDANCE_CONTENT_AREA_DASHBOARD'],
            onFailureRedirect: 'home',
            contextPartnerType: PartnerTypes.SCHOOL,
          },
          loadChildren: () =>
            import('../../../school/content-area/attendance/attendance-cad-routing.module').then(m => m.AttendanceCadRoutingModule),
        },
        {
          matcher: ssvMatcher, // could be '' or :studentId based on existing logic
          component: SchoolSingleColumnLayoutComponent,
          resolve: {
            filter: SchoolSsvFilterResolver,
            sort: SchoolSsvSortResolver,
          },
          data: {
            contextPartnerType: PartnerTypes.SCHOOL,
          },
          loadChildren: () =>
            import('../../../student/student.module').then(m => m.StudentModule),
        },
        {
          path: 'data-grid',
          canActivate: [SyncCombinatorGuard],
          data: {
            guards: [ExploreAllViewGuard],
            contextPartnerType: PartnerTypes.SCHOOL,
          },
          loadChildren: () =>
            import('../../../school/server-side-grid/server-side-grid.module').then(m => m.ServerSideGridModule),
        },
        {
          path: 'notes',
          component: SchoolStandardLayoutComponent,
          data: {
            onFailureRedirect: 'home',
            contextPartnerType: PartnerTypes.SCHOOL,
            origin: 'Notes Inbox',
          },
          loadChildren: () =>
            import('../../../school/notes/notes-page/notes-page.module').then(m => m.NotesPageModule),
        },
        {
          path: 'kims-tracker',
          component: SchoolStandardLayoutComponent,
          canActivate: [KimsTrackerAccessGuard],
          data: {
            contextPartnerType: PartnerTypes.SCHOOL,
          },
          loadChildren: () =>
            import('../../../school/content-area/postsecondary/kims-tracker/kims-tracker.module').then(m => m.KimsTrackerModule),
        },
      ],
    },
  ],
};
